import { getErrorMessage, useServices } from '@/app/providers';
import type { CobranzaFilter, CobranzaSort, CobranzaSummary, UnitCobranza } from '@yawi/types';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Carga el resumen y la lista de unidades con filtro/orden/búsqueda.
 * El filtrado y orden viven en el repositorio (mock en memoria, API en el server).
 * Expone helpers de mutación local para los updates optimistas.
 */
export function useCobranza() {
  const { cobranzaRepo } = useServices();

  const [units, setUnits] = useState<UnitCobranza[]>([]);
  const [summary, setSummary] = useState<CobranzaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<CobranzaFilter>('withDebt');
  const [sort, setSort] = useState<CobranzaSort>('priority');
  const [query, setQuery] = useState('');
  // Debounce de la búsqueda: sin él cada tecla disparaba una petición.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);
  // Solo la última petición escribe el estado (respuestas fuera de orden).
  const requestIdRef = useRef(0);

  const loadUnits = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await cobranzaRepo.getUnits({ filter, sort, query: debouncedQuery });
      if (requestId !== requestIdRef.current) return;
      setUnits(data);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(getErrorMessage(e));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [cobranzaRepo, filter, sort, debouncedQuery]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await cobranzaRepo.getSummary());
    } catch {
      // El resumen es best-effort; un fallo no bloquea la tabla.
    }
  }, [cobranzaRepo]);

  useEffect(() => {
    void loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const patchUnit = useCallback((id: string, patch: Partial<UnitCobranza>) => {
    setUnits((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const patchMany = useCallback((ids: string[], patch: Partial<UnitCobranza>) => {
    const set = new Set(ids);
    setUnits((prev) => prev.map((u) => (set.has(u.id) ? { ...u, ...patch } : u)));
  }, []);

  return {
    units,
    summary,
    loading,
    error,
    filter,
    setFilter,
    sort,
    setSort,
    query,
    setQuery,
    reload: loadUnits,
    refetchSummary: loadSummary,
    patchUnit,
    patchMany,
  };
}
